import { format, isValid, parse } from "date-fns";
import { type EligibilityConditions } from "src/generated/hooks/form-service/requests";
import { z } from "zod";

type Comparison = NonNullable<
  EligibilityConditions["expression"]
>["anyOf"][number]["allOf"][number];
type DateComparison = Extract<Comparison, { kind: "Date" }>;
type DateOperand = DateComparison["left"];

export type DateOperator = "before" | "after" | "between";

export type DateCondition = {
  kind: "date";
  id: string;
  dataPointId: string;
  operator: DateOperator | "";
  date: string;
  endDate: string;
};

export function emptyDateCondition(): DateCondition {
  return {
    kind: "date",
    id: crypto.randomUUID(),
    dataPointId: "",
    operator: "",
    date: "",
    endDate: "",
  };
}

const requiredMessage = "This field is required";

export const dateConditionSchema = z
  .object({
    kind: z.literal("date"),
    id: z.string(),
    dataPointId: z.string().min(1, requiredMessage),
    operator: z.string().min(1, requiredMessage),
    date: z.string(),
    endDate: z.string(),
  })
  .superRefine((condition, ctx) => {
    const needsDate =
      condition.operator === "before" ||
      condition.operator === "after" ||
      condition.operator === "between";
    if (needsDate && condition.date === "") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["date"],
        message: requiredMessage,
      });
    }
    if (condition.operator === "between" && condition.endDate === "") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["endDate"],
        message: requiredMessage,
      });
    }
    if (
      condition.operator === "between" &&
      condition.date !== "" &&
      condition.endDate !== "" &&
      condition.endDate < condition.date
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["date"],
        message: "Start date must be on or before the end date",
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["endDate"],
        message: "End date must be on or after the start date",
      });
    }
  });

export function comparisonToDateCondition(
  comparison: Comparison
): DateCondition | null {
  if (comparison.kind !== "Date") return null;
  if (!isDateReference(comparison.left)) return null;

  if (comparison.operator === "Before" || comparison.operator === "After") {
    if (!isDateLiteral(comparison.right)) return null;
    return {
      kind: "date",
      id: crypto.randomUUID(),
      dataPointId: comparison.left.id,
      operator: comparison.operator === "Before" ? "before" : "after",
      date: comparison.right.value,
      endDate: "",
    };
  }

  if (comparison.operator === "Between") {
    if (!isDateLiteral(comparison.start) || !isDateLiteral(comparison.end)) {
      return null;
    }
    return {
      kind: "date",
      id: crypto.randomUUID(),
      dataPointId: comparison.left.id,
      operator: "between",
      date: comparison.start.value,
      endDate: comparison.end.value,
    };
  }

  return null;
}

export function dateConditionToComparison(
  condition: DateCondition
): DateComparison {
  const left = {
    source: "Reference" as const,
    id: condition.dataPointId,
    type: "Date" as const,
  };
  if (condition.operator === "between") {
    return {
      kind: "Date",
      operator: "Between",
      left,
      start: { source: "Literal", value: condition.date },
      end: { source: "Literal", value: condition.endDate },
    };
  }
  if (condition.operator === "before" || condition.operator === "after") {
    return {
      kind: "Date",
      operator: condition.operator === "before" ? "Before" : "After",
      left,
      right: { source: "Literal", value: condition.date },
    };
  }
  throw new Error("Date condition is incomplete");
}

export function describeDateCondition(
  condition: DateCondition,
  dataPoints: Map<string, string> // id -> name
): string {
  const label = dataPoints.get(condition.dataPointId) ?? "Unknown data point";
  if (condition.operator === "between") {
    return `${label} is between ${formatConditionDate(
      condition.date
    )} and ${formatConditionDate(condition.endDate)}`;
  }
  if (condition.operator === "before" || condition.operator === "after") {
    return `${label} is ${condition.operator} ${formatConditionDate(
      condition.date
    )}`;
  }
  return label;
}

function isDateReference(
  operand: DateOperand
): operand is { source: "Reference"; id: string; type: "Date" } {
  return operand.source === "Reference" && operand.type === "Date";
}

function isDateLiteral(
  operand: DateOperand
): operand is { source: "Literal"; value: string } {
  return operand.source === "Literal" && typeof operand.value === "string";
}

function formatConditionDate(value: string): string {
  const parsed = parse(value, "yyyy-MM-dd", new Date());
  if (!isValid(parsed)) return value;
  return format(parsed, "MMM d, yyyy");
}
